import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaFilePdf, FaUpload, FaBan, FaCheck, FaArrowLeft, FaDownload, FaSearch, FaChevronLeft, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import Modal from "../Modal";

const API = "/api/documentos-oficiales";
const REGISTROS_POR_PAGINA = 6;

export default function DocumentosOficialesAdmin() {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [archivo, setArchivo] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formEdit, setFormEdit] = useState({ titulo: "", descripcion: "" });
  const [archivoEdit, setArchivoEdit] = useState(null);
  const [guardandoEdit, setGuardandoEdit] = useState(false);

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo documentos");
      setDocumentos(await res.json());
    } catch (err) {
      console.error(err);
      mostrarToast("Error cargando documentos", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const subirDocumento = async (e) => {
    e.preventDefault();

    if (!titulo.trim() || !archivo) {
      mostrarToast("Título y archivo PDF son obligatorios", "error");
      return;
    }

    const data = new FormData();
    data.append("titulo", titulo);
    data.append("descripcion", descripcion);
    data.append("archivo", archivo);

    setSubiendo(true);
    try {
      const res = await fetch(API, {
        method: "POST",
        credentials: "include",
        body: data,
      });

      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error subiendo documento", "error");
        return;
      }

      mostrarToast("Documento cargado correctamente");
      setTitulo("");
      setDescripcion("");
      setArchivo(null);
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    } finally {
      setSubiendo(false);
    }
  };

  const cambiarEstado = async (doc) => {
    const activar = !doc.activo;
    try {
      const res = await fetch(`${API}/${doc.id_documento}${activar ? "/reactivar" : ""}`, {
        method: activar ? "PATCH" : "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error actualizando documento", "error");
        return;
      }

      mostrarToast(activar ? "Documento reactivado" : "Documento desactivado");
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    }
  };

  const abrirModalReemplazar = (doc) => {
    setEditando(doc);
    setFormEdit({ titulo: doc.titulo, descripcion: doc.descripcion || "" });
    setArchivoEdit(null);
    setModalOpen(true);
  };

  const cerrarModal = () => setModalOpen(false);

  const guardarReemplazo = async () => {
    if (!formEdit.titulo.trim()) {
      mostrarToast("El título es obligatorio", "error");
      return;
    }

    const data = new FormData();
    data.append("titulo", formEdit.titulo);
    data.append("descripcion", formEdit.descripcion);
    if (archivoEdit) data.append("archivo", archivoEdit);

    setGuardandoEdit(true);
    try {
      const res = await fetch(`${API}/${editando.id_documento}`, {
        method: "PUT",
        credentials: "include",
        body: data,
      });

      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error actualizando documento", "error");
        return;
      }

      mostrarToast("Documento actualizado correctamente");
      setModalOpen(false);
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    } finally {
      setGuardandoEdit(false);
    }
  };

  const documentosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return documentos;
    return documentos.filter((d) => d.titulo?.toLowerCase().includes(termino));
  }, [documentos, busqueda]);

  const totalPaginas = Math.ceil(documentosFiltrados.length / REGISTROS_POR_PAGINA);
  const inicio = (pagina - 1) * REGISTROS_POR_PAGINA;
  const fin = inicio + REGISTROS_POR_PAGINA;
  const documentosPagina = documentosFiltrados.slice(inicio, fin);
  const inicioRango = documentosFiltrados.length === 0 ? 0 : inicio + 1;
  const finRango = Math.min(fin, documentosFiltrados.length);

  return (
    <div className="p-6 bg-slate-50">

      <div className="mb-6">
        <Link to="/dashboard/administracion" className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1 mb-1">
          <FaArrowLeft className="text-xs" /> Administración
        </Link>
        <h1 className="text-2xl font-bold text-slate-800">Documentos Oficiales</h1>
        <p className="text-sm text-slate-500">Sube y administra los documentos oficiales en PDF de la iglesia.</p>
      </div>

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">

        {/* Formulario de carga */}
        <form onSubmit={subirDocumento} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-fit">
          <h2 className="text-base font-semibold text-slate-800 mb-4">Subir nuevo documento</h2>

          <div className="flex flex-col gap-3">
            <input
              type="text"
              placeholder="Título del documento"
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />

            <textarea
              placeholder="Descripción (opcional)"
              rows={3}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Archivo PDF</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                className="text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700 file:text-xs file:font-medium hover:file:bg-indigo-100"
              />
            </div>

            <button
              type="submit"
              disabled={subiendo}
              className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition mt-2"
            >
              <FaUpload /> {subiendo ? "Subiendo..." : "Subir documento"}
            </button>
          </div>
        </form>

        {/* Lista de documentos */}
        <div className="lg:col-span-2">

          {/* BUSCADOR */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
              <input
                type="text"
                placeholder="Buscar por nombre del documento..."
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPagina(1);
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 divide-y divide-slate-100">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-10">Cargando documentos...</p>
          ) : documentosFiltrados.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">
              {busqueda ? "No se encontraron documentos con ese nombre." : "Aún no se ha cargado ningún documento."}
            </p>
          ) : (
            documentosPagina.map((d) => (
              <div key={d.id_documento} className="flex items-center justify-between gap-4 p-4 hover:bg-slate-50 transition">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center shrink-0">
                    <FaFilePdf />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm text-slate-800 truncate">{d.titulo}</p>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                        d.activo ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
                      }`}>
                        {d.activo ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                    {d.descripcion && <p className="text-xs text-slate-500 truncate">{d.descripcion}</p>}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`${d.archivo}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                  >
                    <FaDownload className="text-[10px]" /> Ver
                  </a>
                  <button
                    onClick={() => abrirModalReemplazar(d)}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                  >
                    <FaSyncAlt className="text-[10px]" /> Reemplazar
                  </button>
                  <button
                    onClick={() => cambiarEstado(d)}
                    className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border transition ${
                      d.activo
                        ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                        : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                    }`}
                  >
                    {d.activo ? <><FaBan className="text-[10px]" /> Desactivar</> : <><FaCheck className="text-[10px]" /> Activar</>}
                  </button>
                </div>
              </div>
            ))
          )}
          </div>

          {/* PAGINACIÓN */}
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-slate-500">
              {documentosFiltrados.length === 0
                ? "Sin resultados"
                : `Mostrando ${inicioRango} a ${finRango} de ${documentosFiltrados.length} resultados`}
            </span>

            {totalPaginas > 1 && (
              <div className="flex items-center gap-2">
                <button
                  disabled={pagina === 1}
                  onClick={() => setPagina(pagina - 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
                >
                  <FaChevronLeft className="text-xs" />
                </button>

                {Array.from({ length: totalPaginas }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setPagina(i + 1)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-semibold transition ${
                      pagina === i + 1
                        ? "bg-indigo-600 text-white"
                        : "border border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  disabled={pagina === totalPaginas}
                  onClick={() => setPagina(pagina + 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
                >
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Modal reemplazar documento */}
      <Modal open={modalOpen} onClose={cerrarModal} title="Reemplazar documento" maxWidth="max-w-lg">
        <div>
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Título del documento</label>
              <input
                type="text"
                placeholder="Título del documento"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                value={formEdit.titulo}
                onChange={(e) => setFormEdit({ ...formEdit, titulo: e.target.value })}
              />
            </div>

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Descripción (opcional)</label>
              <textarea
                placeholder="Descripción (opcional)"
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none"
                value={formEdit.descripcion}
                onChange={(e) => setFormEdit({ ...formEdit, descripcion: e.target.value })}
              />
            </div>

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Nuevo archivo PDF (opcional)</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setArchivoEdit(e.target.files?.[0] || null)}
                className="text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700 file:text-xs file:font-medium hover:file:bg-indigo-100"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Si no seleccionas un archivo nuevo, se conserva el PDF actual y solo se actualizan el título y la descripción.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
            <button onClick={cerrarModal} className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
              Cancelar
            </button>
            <button
              onClick={guardarReemplazo}
              disabled={guardandoEdit}
              className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-medium transition"
            >
              {guardandoEdit ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
